import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonComment } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Comment } from "@shared/models/comment"
import Patch from "@shared/models/patch-updater"
import { assign, find, get } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  cardId: string
  comment: Comment
  commenterName: string
  deletedAt: string
}

export const LESSON_CARD_REMOVE_COMMENT = {
  name: "LESSON_CARD_REMOVE_COMMENT",

  patches(payload: Payload) {
    const cardPatch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.cardId,
    }).pull("embedded", "attributes.commentIds", payload.comment.id).patch

    const commentPatch = Patch.create("comment", payload.comment.id).set(
      "attributes.deletedAt",
      payload.deletedAt
    ).patch

    return [cardPatch, commentPatch]
  },

  undoPatches(payload: Payload) {
    const cardPatch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.cardId,
    }).addToSet("embedded", "attributes.commentIds", payload.comment.id).patch

    const commentPatch = Patch.create("comment", payload.comment.id).set(
      "attributes.deletedAt",
      null
    ).patch

    return [commentPatch, cardPatch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let card = find(get(models, "lesson.attributes.cards"), {
      id: payload.cardId,
    })
    let cardTitle = get(card, "title")
    let context = defaultLessonContext(payload, models)
    let commenterName = payload.commenterName
    let commenterId = payload.comment.attributes.commenterId
    let newContext = assign({}, context, {
      objectType: "card",
      cardTitle: cardTitle,
      commenterName,
      commenterId,
      isLegacy: false,
    })
    let newEvent: LessonComment = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      primary_object_id: payload.cardStackId,
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "comment",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "cards",
      adverbial_verb: "removing",
      adverbial_object_type: "comment",
      adverbial_object_specifier: null,
      adverbial_object_id: payload.comment.id,
      adverbial_object_id_and_ancestor_ids: [
        ...models.cardStackIdAndAncestorIds,
        payload.comment.id,
      ],
    }

    return {
      title: "Removed a Comment",
      titleForAnalytics: "Comment - Remove",
      description: `Removed a comment from the "${cardTitle}" card`,
      context: newContext,
      activity: defaultLessonActivity(payload, models, "removed"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_CARD_REMOVE_COMMENT
